import React, { useState, useEffect, useContext } from 'react';

import CurrencyInput from 'react-currency-input-field';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';

/** components */
import { MultipleSelect } from './';
import LabelWithTooltip from './LabelWithTooltip';

/** config */
import TenantConfig from '../../config';

/** context */
import { UserContext } from '../../context/UserContext';

/** helpers */
import { sanitizeHtml } from '../../helpers/format';
import { useForm } from '../../hooks/useForm';

/** service */
import { updateApplication, validateProperty } from '../../service/api';
import { ScrollToTop } from '../ScrollToTop';

const LoanPurpose = ({
  setError,
  toggleFieldModal,
  isModalOpen,
  initializeSectionError,
  handleContinue,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = new URLSearchParams(location.search).get('edit');

  const { getPropertyFields } = useForm();
  const { appData, updateAppData, setLoading, width } = useContext(UserContext);
  const loanPurposeFeature = appData?.applicationFeatures.find(
    (feature) => feature.dataType === 'loanPurposeDetails'
  );

  const [data, setData] = useState(loanPurposeFeature?.value);
  const [dataError, setDataError] = useState({});

  const fields = getPropertyFields(loanPurposeFeature, loanPurposeFeature.value);
  let loanPurposeField = fields.find((field) => field.name == 'loanPurpose');

  const percentageDownEstimateMap = {
    'Leasehold Improvements': 10,
    'Equipment Purchase': 10,
    'Purchase a Business - Asset Purchase': 10,
    'Purchase a Business - Stock Purchase': 10,
    'Fund the start up of this business': 10,
    'Add an Addition to an Existing Building': 10,
    'Make Renovations to an Existing Building': 10,
    'Purchase Land and Improvements': 10,
    'Construct a Building': 10,
    'Furniture and Fixtures Purchase': 5,
    'Inventory Purchase': 5,
  };

  useEffect(() => {
    calculateSumResult();
    ReactTooltip.rebuild();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!loanPurposeFeature) {
    return;
  }

  const calculateSumResult = () => {
    // calculate totalNeeded & totalDownPayment
    let resTotalNeeded = 0;
    let resTotalDownPayment = 0;
    if (data == undefined) {
      return;
    }
    data?.loanPurpose?.map((condition) => {
      let costField = fields.find(
        (field) =>
          field.dependsOn === 'loanPurpose' &&
          field.condition === condition &&
          field.name.includes('Cost') &&
          !field.name.includes('actual')
      );

      let currency = costField ? data[costField.name] : null;
      if (currency) {
        let valueTotalNeeded = Number(currency?.toString().replace(/[^0-9.-]+/g, ''));
        // get percent
        let percent = percentageDownEstimateMap.hasOwnProperty(costField.condition)
          ? percentageDownEstimateMap[costField.condition]
          : 0;
        resTotalNeeded += valueTotalNeeded;
        resTotalDownPayment += calculateDownPayment(valueTotalNeeded, percent);
      }
    });

    // calculate estimatedLoanAmount
    let resEstimatedLoanAmount = 0;
    resEstimatedLoanAmount =
      resTotalNeeded == 0
        ? 0
        : resTotalNeeded -
          resTotalDownPayment +
          ((resTotalNeeded + resTotalDownPayment) * 2) / 100 +
          5000;

    if (data?.totalNeeded !== resTotalNeeded) {
      setData({
        ...data,
        totalNeeded: resTotalNeeded,
        totalDownPayment: resTotalDownPayment,
        estimatedLoanAmount: resEstimatedLoanAmount,
      });
    }
  };

  const calculateDownPayment = (cost, percent) => {
    return cost === undefined ? '' : (cost * percent) / 100;
  };

  const handleChangeOption = (value) => {
    // add/remove option in data['loanPurpose']
    setDataError({});
    let options = data?.hasOwnProperty('loanPurpose') ? data['loanPurpose'] : [];
    if (options.includes(value)) {
      options = options.filter((item) => item !== value);

      // remove cost/down payment in data
      const pairFieldsToRemove = fields.filter((field) => field.condition === value);
      pairFieldsToRemove.map((fieldToRemove) => {
        delete data[fieldToRemove.name];
        // remove error message
        delete dataError[fieldToRemove.name];
      });
    } else {
      options.push(value);
    }
    setData({
      ...data,
      loanPurpose: options,
    });
  };

  const renderFieldsOption = (option) => {
    let checked =
      data?.hasOwnProperty('loanPurpose') && data['loanPurpose'].includes(option.value)
        ? true
        : false;

    return (
      <div className="formCustomCheckBox loan-purpose-option" key={'option' + option.value}>
        <label htmlFor={option.label}>
          <input
            id={option.label}
            name={option.label}
            checked={checked}
            type="checkbox"
            onChange={() => handleChangeOption(option.value)}
          />
          <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(option.label) }}></span>
        </label>
      </div>
    );
  };

  // Handler for currency input
  const handleCurrenyChange = (value, name, field) => {
    initializeSectionError();
    let valueChanged = value;
    if (value > field.limits.maximum) {
      valueChanged = value.substring(0, value.length - 1);
    }

    // validation
    if (parseFloat(valueChanged) == 0) {
      setDataError((dataError) => ({
        ...dataError,
        [name]: 'The Total Need should be greater than $0.00',
      }));
    } else if (!valueChanged) {
      setDataError((dataError) => ({
        ...dataError,
        [name]: 'This is a required field',
      }));
      setData({ ...data, [name]: null });
    } else {
      if (dataError.hasOwnProperty(name)) {
        delete dataError[name];
      }

      let focusedFieldsPair = fields.filter((f) => f.condition === field.condition);
      if (focusedFieldsPair.length > 1) {
        // set cost & downpayment
        // get percent
        let percent = percentageDownEstimateMap.hasOwnProperty(focusedFieldsPair[1].condition)
          ? percentageDownEstimateMap[focusedFieldsPair[1].condition]
          : 0;
        let valueDownPayment = calculateDownPayment(valueChanged, percent);
        setData({ ...data, [name]: valueChanged, [focusedFieldsPair[1].name]: valueDownPayment });
      } else {
        // set cost
        setData({ ...data, [name]: valueChanged });
      }
    }
  };

  const renderDesktopFieldsPair = (pairFields) => {
    return (
      <div key={pairFields[0].name} className="form-group loan-purpose-group">
        <div>
          <label htmlFor={`pair-field-${pairFields[0].name}`}>{pairFields[0].condition}</label>
        </div>
        <div className={`${dataError[pairFields[0].name] ? 'error' : ''}`}>
          <CurrencyInput
            id={pairFields[0].name}
            name={pairFields[0].name}
            placeholder={pairFields[0].placeholder}
            value={data[pairFields[0].name]}
            decimalScale={2}
            onValueChange={(value, name) => handleCurrenyChange(value, name, pairFields[0])}
            className={`property_field_cost ${dataError[pairFields[0].name] ? 'error' : ''}`}
            intlConfig={{ locale: 'en-US', currency: 'USD' }}
            spellCheck={false}
          />
          {dataError[pairFields[0].name] && (
            <p className="error-text">{dataError[pairFields[0].name]}</p>
          )}
        </div>
        <div className={`${dataError[pairFields[1].name] ? 'error' : ''}`}>
          <CurrencyInput
            id={pairFields[1].name}
            name={pairFields[1].name}
            placeholder={pairFields[1].placeholder}
            value={data[pairFields[1].name]}
            decimalScale={2}
            className={dataError[pairFields[1].name] ? 'error' : ''}
            intlConfig={{ locale: 'en-US', currency: 'USD' }}
            disabled
          />
          {dataError[pairFields[1].name] && (
            <p className="error-text">{dataError[pairFields[1].name]}</p>
          )}
        </div>
      </div>
    );
  };

  const renderMobileFieldsPair = (pairFields) => {
    return (
      <div key={pairFields[0].name} className="form-group loan-purpose-group">
        <div>
          <label htmlFor={`pair-field-${pairFields[0].name}`}>{pairFields[0].condition}</label>
        </div>
        <div className="title">
          <label htmlFor="Total Need">Total Need*</label>
        </div>
        <div className={`${dataError[pairFields[0].name] ? 'error' : ''}`}>
          <CurrencyInput
            id={pairFields[0].name}
            name={pairFields[0].name}
            placeholder={pairFields[0].placeholder}
            value={data[pairFields[0].name]}
            decimalScale={2}
            onValueChange={(value, name) => handleCurrenyChange(value, name, pairFields[0])}
            className={`property_field_cost ${dataError[pairFields[0].name] ? 'error' : ''}`}
            intlConfig={{ locale: 'en-US', currency: 'USD' }}
            spellCheck={false}
          />
          {dataError[pairFields[0].name] && (
            <p className="error-text">{dataError[pairFields[0].name]}</p>
          )}
        </div>
        <div className="title">
          <label htmlFor="Estimated Down-Payment">Estimated Down-Payment*</label>
        </div>
        <div className={`${dataError[pairFields[1].name] ? 'error' : ''}`}>
          <CurrencyInput
            id={pairFields[1].name}
            name={pairFields[1].name}
            placeholder={pairFields[1].placeholder}
            value={data[pairFields[1].name]}
            decimalScale={2}
            className={dataError[pairFields[1].name] ? 'error' : ''}
            intlConfig={{ locale: 'en-US', currency: 'USD' }}
            disabled
            spellCheck={false}
          />
          {dataError[pairFields[1].name] && (
            <p className="error-text">{dataError[pairFields[1].name]}</p>
          )}
        </div>
      </div>
    );
  };

  const renderOptionFields = () => {
    return loanPurposeField?.options.map((option) => renderFieldsOption(option));
  };

  const renderDesktopPropertyFields = (arrDollarInputFieldsPaired, fieldClassName, fieldProps) => {
    return (
      <>
        <div className="form-group loan-purpose-group">
          <div></div>
          <div className="title">
            <label htmlFor="Total Need">Total Need*</label>
          </div>
          <div className="title">
            <label htmlFor="Estimated Down-Payment">Estimated Down-Payment*</label>
          </div>
        </div>
        {arrDollarInputFieldsPaired.map((pairFields) => renderDesktopFieldsPair(pairFields))}
        <div className="form-group loan-purpose-group">
          <div>
            <label htmlFor="Sum">Sum</label>
          </div>
          <div>
            <CurrencyInput
              id="total_needed"
              name="total_needed"
              placeholder="Sum of total needed fields"
              value={data?.totalNeeded}
              decimalScale={2}
              intlConfig={{ locale: 'en-US', currency: 'USD' }}
              disabled
              spellCheck={false}
            />
          </div>
          <div>
            <CurrencyInput
              id="sum_down_payment"
              name="sum_down_payment"
              placeholder="Sum of estimated down payment"
              value={data?.totalDownPayment}
              decimalScale={2}
              intlConfig={{ locale: 'en-US', currency: 'USD' }}
              disabled
              spellCheck={false}
            />
          </div>
        </div>
        <div className="form-group loan-purpose-group">
          <div>
            <label htmlFor="General">General</label>
          </div>
          <div>
            <LabelWithTooltip
              field={fields.find((field) => field.name === 'estimatedLoanAmount')}
              toggleFieldModal={toggleFieldModal}
              isModalOpen={isModalOpen}
            />
            <CurrencyInput
              id="estimated_loan_amount"
              name="estimated_loan_amount"
              placeholder="Estimated Proposed Need"
              value={data?.estimatedLoanAmount}
              decimalScale={2}
              intlConfig={{ locale: 'en-US', currency: 'USD' }}
              disabled
              spellCheck={false}
            />
          </div>
          <div>
            <MultipleSelect {...fieldProps} fieldClassName={fieldClassName} />
          </div>
        </div>
      </>
    );
  };

  const renderMobilePropertyFields = (arrDollarInputFieldsPaired, fieldClassName, fieldProps) => {
    return (
      <>
        {arrDollarInputFieldsPaired.map((pairFields) => renderMobileFieldsPair(pairFields))}
        <div className="form-group loan-purpose-group">
          <div>
            <label htmlFor="Sum">Sum</label>
          </div>
          <div className="title">
            <label htmlFor="Total Need">Total Need*</label>
          </div>
          <div>
            <CurrencyInput
              id="total_needed"
              name="total_needed"
              placeholder="Sum of total needed fields"
              value={data?.totalNeeded}
              decimalScale={2}
              intlConfig={{ locale: 'en-US', currency: 'USD' }}
              disabled
              spellCheck={false}
            />
          </div>
          <div className="title">
            <label htmlFor="Estimated Down-Payment">Estimated Down-Payment*</label>
          </div>
          <div>
            <CurrencyInput
              id="sum_down_payment"
              name="sum_down_payment"
              placeholder="Sum of estimated down payment"
              value={data?.totalDownPayment}
              decimalScale={2}
              intlConfig={{ locale: 'en-US', currency: 'USD' }}
              disabled
              spellCheck={false}
            />
          </div>
        </div>
        <div className="form-group loan-purpose-group">
          <div>
            <label htmlFor="General">General</label>
          </div>
          <div>
            <LabelWithTooltip
              field={fields.find((field) => field.name === 'estimatedLoanAmount')}
              toggleFieldModal={toggleFieldModal}
              isModalOpen={isModalOpen}
            />
            <CurrencyInput
              id="estimated_loan_amount"
              name="estimated_loan_amount"
              placeholder="Estimated Proposed Need"
              value={data?.estimatedLoanAmount}
              decimalScale={2}
              intlConfig={{ locale: 'en-US', currency: 'USD' }}
              disabled
              spellCheck={false}
            />
          </div>
          <div>
            <MultipleSelect {...fieldProps} fieldClassName={fieldClassName} />
          </div>
        </div>
      </>
    );
  };

  const renderPropertyFields = () => {
    let arrDollarInputFieldsPaired = [];

    data?.loanPurpose?.map((condition) => {
      let pairFields = fields.filter(
        (field) =>
          field.dependsOn === 'loanPurpose' &&
          field.condition === condition &&
          !field.name.startsWith('actual')
      );
      if (pairFields && pairFields.length > 0) arrDollarInputFieldsPaired.push(pairFields);
    });

    const fieldDownPaymentSource = fields.find((field) => field.name === 'downPaymentSource');
    const fieldClassName = `${dataError[fieldDownPaymentSource?.name] ? 'error' : ''}`;

    const fieldProps = {
      data,
      setData,
      dataError,
      setDataError,
      field: fieldDownPaymentSource,
      toggleFieldModal,
      isModalOpen,
      initializeSectionError,
    };

    return width > 768
      ? renderDesktopPropertyFields(arrDollarInputFieldsPaired, fieldClassName, fieldProps)
      : renderMobilePropertyFields(arrDollarInputFieldsPaired, fieldClassName, fieldProps);
  };

  const handleSubmit = async (goToDashboard) => {
    setLoading(true);
    setDataError({});
    // validation
    let statusValidation = true;
    if (!goToDashboard) {
      // frontend validation
      data?.loanPurpose?.map((condition) => {
        let pairFields = fields.filter(
          (field) => field.dependsOn === 'loanPurpose' && field.condition === condition
        );
        let fieldName = pairFields[0].name;
        if (!data.hasOwnProperty(fieldName) || data[fieldName] == null) {
          if (!dataError.hasOwnProperty(fieldName)) {
            setDataError((dataError) => ({
              ...dataError,
              [fieldName]: 'This is a requried field',
            }));
          }
          statusValidation = false;
        }
      });
    }
    if (
      !goToDashboard &&
      data.totalDownPayment > 0 &&
      (!data?.downPaymentSource || data?.downPaymentSource?.length === 0)
    ) {
      setDataError((dataError) => ({
        ...dataError,
        downPaymentSource: 'This is a required field ',
      }));
      statusValidation = false;
    }

    if (statusValidation) {
      // when frontend validation is passed
      try {
        // update applicant instance
        const updatedData = await updateApplication([
          {
            itemType: loanPurposeFeature.itemType,
            dataType: loanPurposeFeature.dataType,
            name: loanPurposeFeature.name,
            value: data,
          },
        ]);

        if (updatedData.data) {
          updateAppData(updatedData.data);
        }
        if (goToDashboard) {
          navigate('/dashboard');
          return;
        }
        // backend validation
        const validationData = await validateProperty(loanPurposeFeature.name);
        setDataError({});

        if (validationData.data && validationData.data.errors) {
          if (validationData.data.errors.length) {
            validationData.data.errors.map((error) => {
              const attributes = error.property.split('.');
              setDataError((dataError) => ({
                ...dataError,
                [attributes[1]]: dataError[attributes[1]]
                  ? dataError[attributes[1]].concat('. ', error.message)
                  : error.message,
              }));
            });
            setLoading(false);
            return false;
          }
        }
      } catch (error) {
        console.log('error', error.message);
        if (error?.response?.data?.errors && error.response.data.errors.length) {
          setError(error.response.data.errors[0].message);
        }
        setLoading(false);
        return;
      }

      // continue next step
      setLoading(false);
      if (isEdit) {
        navigate('/review');
      }
      handleContinue();
    } else {
      setLoading(false);
    }
  };

  return (
    <ScrollToTop>
      <div className="loan-purpose">
        <h3>{loanPurposeField?.label}</h3>
        <div className="loan-purpose-options">{renderOptionFields()}</div>
        {data?.hasOwnProperty('loanPurpose') && data['loanPurpose'].length > 0
          ? renderPropertyFields()
          : ''}
        <div className={'d-flex bottom-actions align-items-center justify-content-around'}>
          <div className="actions d-flex">
            {!isEdit && !!TenantConfig?.settings?.isSaveAndContinueButtonEnabled ? (
              <button onClick={() => handleSubmit(true)} className="primary-btn">
                Save and Continue Later
              </button>
            ) : (
              ''
            )}
            <button
              id="continue-btn"
              onClick={() => handleSubmit(false)}
              className="primary-btn"
              disabled={
                !data || !data.hasOwnProperty('loanPurpose') || data['loanPurpose'].length === 0
              }
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </ScrollToTop>
  );
};

export default LoanPurpose;
